import { useEffect, useRef } from 'react';

type KeyHandler = (event: KeyboardEvent) => void;

export interface KeyboardOptions {
  // Om true: kör handler även när fokus är i input/textarea. Default false.
  whenTyping?: boolean;
  // Om false: hooka inte alls (för conditional shortcuts).
  enabled?: boolean;
}

// Liten hjälpare för window-keydown-shortcuts. Default ignoreras keys när
// användaren skriver i input/textarea/contenteditable så vi inte stör skrift.
export function useKeyboard(handler: KeyHandler, opts: KeyboardOptions = {}) {
  const { whenTyping = false, enabled = true } = opts;
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (!whenTyping) {
        const target = event.target as HTMLElement | null;
        if (target) {
          const tag = target.tagName;
          if (
            tag === 'INPUT' ||
            tag === 'TEXTAREA' ||
            tag === 'SELECT' ||
            target.isContentEditable
          ) {
            return;
          }
        }
      }
      handlerRef.current(event);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled, whenTyping]);
}
